/**
 * SideBySideViewer — Phase 3.
 *
 * Side-by-side split between the in-house Canvas2D point cloud renderer (left)
 * and the the Panorix 3D viewer (right). Cameras are kept in sync bidirectionally:
 *
 *   Forge ──► PointCloud : on Forge CAMERA_CHANGE_EVENT, derive azimuth/elevation
 *                          from (target - position) and project the radial distance
 *                          into the point-cloud zoom scalar.
 *   PointCloud ──► Forge : on user-driven orbit, drive Forge navigation.setView()
 *                          around its current pivot using the matching angles.
 *
 * Since the two scenes live in different world frames (the scan is normalized to
 * [-1, 1] in PointCloudViewer; the Forge model lives in its native units),
 * only the view-direction angles and zoom factor are mirrored, not absolute
 * positions. This gives a "same viewing angle / same zoom" experience without
 * requiring an alignment transform.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import PointCloudViewer from '@/components/scan3d/PointCloudViewer';
import ForgeViewerWrapper from '@/components/scan3d/ForgeViewerWrapper';
import { Link, Link2Off, Eye, Box } from 'lucide-react';

// Empirical zoom factor that maps a Forge radius (model units) to PointCloudViewer's
// orbit zoom scalar. Tuned for plant-scale models (10–100 m extent); adjust if scenes
// fall outside that range.
const FORGE_ZOOM_FACTOR = 50;

function _forgeToOrbit(viewer) {
  // Derive rx (azimuth around Y), ry (elevation), and z (zoom scalar) from Forge cam
  try {
    const nav = viewer.navigation;
    const pos = nav.getPosition();
    const tgt = nav.getTarget();
    const dx = pos.x - tgt.x;
    const dy = pos.y - tgt.y;
    const dz = pos.z - tgt.z;
    const radius = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
    // Map: rx = atan2(dx, dz)  ; ry = asin(dy / radius)
    const rx = Math.atan2(dx, dz);
    const ry = Math.max(-1.4, Math.min(1.4, Math.asin(dy / radius)));
    // Zoom: tighter Forge radius = larger orbit zoom factor. Magic number lives
    // at top of file (FORGE_ZOOM_FACTOR), clamped to PointCloudViewer's [0.3, 8].
    const z = Math.max(0.3, Math.min(8, FORGE_ZOOM_FACTOR / radius));
    return { rx, ry, z, radius };
  } catch (_) {
    return null;
  }
}

function _orbitToForge(viewer, orbit) {
  try {
    const nav = viewer.navigation;
    const tgt = nav.getTarget();
    // Recover prior radius from current Forge camera so we don't yo-yo the zoom
    const prevPos = nav.getPosition();
    const rdx = prevPos.x - tgt.x;
    const rdy = prevPos.y - tgt.y;
    const rdz = prevPos.z - tgt.z;
    const radius = Math.sqrt(rdx * rdx + rdy * rdy + rdz * rdz) || 1;
    const { rx, ry } = orbit;
    const dx = radius * Math.cos(ry) * Math.sin(rx);
    const dy = radius * Math.sin(ry);
    const dz = radius * Math.cos(ry) * Math.cos(rx);
    nav.setView(
      { x: tgt.x + dx, y: tgt.y + dy, z: tgt.z + dz },
      { x: tgt.x, y: tgt.y, z: tgt.z }
    );
  } catch (_) { /* ignore */ }
}

export default function SideBySideViewer({ projectId }) {
  const [scans, setScans] = useState([]);
  const [models, setModels] = useState([]);
  const [scanId, setScanId] = useState('');
  const [modelId, setModelId] = useState('');
  const [model, setModel] = useState(null);
  const [scanSession, setScanSession] = useState(null);
  const [syncCam, setSyncCam] = useState(true);
  const [cameraNote, setCameraNote] = useState(null);

  const pcRef = useRef(null);
  const forgeRef = useRef(null);
  // Per-component reentrancy lock — prevents the two-way camera sync from
  // feedback-looping. Component-scoped so two SideBySideViewers in the same
  // page don't share a single lock.
  const lockRef = useRef(false);

  // Initial lists
  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const [s, m] = await Promise.all([
          api.get(`/v1/projects/${projectId}/scan3d/sessions?limit=50`),
          api.get(`/v1/projects/${projectId}/scan3d/models?isLatest=true`),
        ]);
        setScans(s.data?.sessions || s.data?.items || []);
        setModels((m.data?.models || []).filter(x => x.translationStatus === 'SUCCESS'));
      } catch (_) { toast.error('Failed to load lists'); }
    })();
  }, [projectId]);

  // Resolve selections
  useEffect(() => {
    if (modelId) {
      api.get(`/v1/projects/${projectId}/scan3d/models/${modelId}`)
        .then(r => setModel(r.data))
        .catch(() => setModel(null));
    } else setModel(null);
  }, [modelId, projectId]);

  useEffect(() => {
    if (scanId) {
      const sess = scans.find(s => (s.id || s.sessionCode) === scanId);
      setScanSession(sess);
    } else setScanSession(null);
  }, [scanId, scans]);

  // Compute apiUrl for the scan point cloud (picks first rawFile of the session)
  const scanApiUrl = useMemo(() => {
    if (!scanSession || !projectId) return null;
    const rawFile = (scanSession.rawFiles || [])[0];
    if (!rawFile) return null;
    const fileId = rawFile.fileId || rawFile.id || rawFile._id;
    if (!fileId) return null;
    const sid = scanSession.id || scanSession.sessionCode;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    return `${base}/api/v1/projects/${projectId}/scan3d/sessions/${sid}/files/${fileId}/points-bin?limit=2000000`;
  }, [scanSession, projectId]);

  // Forge → PointCloud sync
  const onForgeCamera = (v) => {
    forgeRef.current = v;
    if (!syncCam || lockRef.current) return;
    const orbit = _forgeToOrbit(v);
    if (!orbit || !pcRef.current) return;
    lockRef.current = true;
    try {
      pcRef.current.setOrbit(orbit);
      setCameraNote(`Model → PC : rx=${orbit.rx.toFixed(2)} ry=${orbit.ry.toFixed(2)} z=${orbit.z.toFixed(2)}`);
    } finally {
      setTimeout(() => { lockRef.current = false; }, 30);
    }
  };

  // PointCloud → Forge sync (fires only on user orbit, not on programmatic setOrbit)
  const onPcOrbit = (orbit) => {
    if (!syncCam || lockRef.current || !forgeRef.current) return;
    lockRef.current = true;
    try {
      _orbitToForge(forgeRef.current, orbit);
      setCameraNote(`PC → Model : rx=${orbit.rx.toFixed(2)} ry=${orbit.ry.toFixed(2)} z=${orbit.z.toFixed(2)}`);
    } finally {
      setTimeout(() => { lockRef.current = false; }, 30);
    }
  };

  return (
    <div className="space-y-3" data-testid="scan3d-sidebyside-tab">
      {/* Controls */}
      <div className="rounded-xl p-3 flex flex-wrap items-end gap-2" style={{ background: '#0f1722', border: '1px solid #1f2937' }}>
        <label className="text-xs">
          <span className="opacity-70">Scan</span>
          <select value={scanId} onChange={e => setScanId(e.target.value)}
            className="block mt-1 px-2 py-1 rounded" style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }}
            data-testid="sidebyside-scan-select">
            <option value="">— select —</option>
            {scans.map(s => <option key={s.id || s.sessionCode} value={s.id || s.sessionCode}>{s.sessionCode} · {s.area || s.purpose}</option>)}
          </select>
        </label>
        <label className="text-xs">
          <span className="opacity-70">Design model</span>
          <select value={modelId} onChange={e => setModelId(e.target.value)}
            className="block mt-1 px-2 py-1 rounded" style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }}
            data-testid="sidebyside-model-select">
            <option value="">— select —</option>
            {models.map(m => <option key={m.id} value={m.id}>{m.modelCode} · {m.modelName}</option>)}
          </select>
        </label>
        <button onClick={() => setSyncCam(s => !s)}
          className="flex items-center gap-1 px-2 py-1.5 rounded text-xs ml-auto"
          style={{
            background: syncCam ? '#10b98122' : '#0a0e14',
            color: syncCam ? '#10b981' : '#9ca3af',
            border: '1px solid #1f2937',
          }}
          data-testid="sidebyside-cam-sync-btn">
          {syncCam ? <Link className="w-3 h-3" /> : <Link2Off className="w-3 h-3" />}
          {syncCam ? 'Camera Sync ON' : 'Camera Sync OFF'}
        </button>
      </div>

      {/* Split panes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="space-y-1" data-testid="sidebyside-scan-pane">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider opacity-60">
            <Eye className="w-3 h-3" /> Scan Point Cloud
          </div>
          <div className="rounded-xl overflow-hidden" style={{ background: '#0a0e14', border: '1px solid #1f2937', minHeight: 500 }}>
            {scanApiUrl ? (
              <PointCloudViewer ref={pcRef} apiUrl={scanApiUrl} height={500}
                                onOrbitChange={onPcOrbit} />
            ) : (
              <div className="h-[500px] flex items-center justify-center text-sm opacity-60">
                {scanSession ? 'No raw files on this session' : 'Pick a scan'}
              </div>
            )}
          </div>
        </div>
        <div className="space-y-1" data-testid="sidebyside-model-pane">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider opacity-60">
            <Box className="w-3 h-3" /> Design Model — 3D Viewer
          </div>
          {model ? (
            <ForgeViewerWrapper projectId={projectId} urn={model.svf2Urn || model.apsUrn}
              onCameraChange={onForgeCamera} height={500} testId="sidebyside-forge-viewer" />
          ) : (
            <div className="h-[500px] rounded-xl flex items-center justify-center text-sm opacity-60"
              style={{ background: '#0a0e14', border: '1px solid #1f2937' }}>Pick a model</div>
          )}
        </div>
      </div>

      {cameraNote && (
        <div className="text-[10px] opacity-50 font-mono" data-testid="sidebyside-cam-note">{cameraNote}</div>
      )}
    </div>
  );
}
